import { useState, useEffect, useRef } from "react"
import { Link } from "react-router-dom"
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable,
  useDraggable,
  type DragStartEvent,
  type DragEndEvent,
} from "@dnd-kit/core"

import { Button } from "@/components/ui/button"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import {
  EyeIcon,
  PencilIcon,
  Trash2Icon,
  ArrowRightIcon,
  GripVerticalIcon,
  PlayIcon,
  CheckCheckIcon,
  CalendarCheckIcon,
  CircleCheckIcon,
  TruckIcon,
  MessageCircleIcon,
  UserIcon,
  CalendarIcon,
  AlertTriangleIcon,
  RouteIcon,
  PackageIcon,
  InfoIcon,
} from "lucide-react"
import { cn } from "@/lib/utils"
import { avatarInitials, avatarColor } from "@/lib/avatar"
import { shipmentAlertLevel, ALERT_LEVEL_CARD_CLASSES, ALERT_LEVEL_BADGE_CLASSES, alertBadgeLabel, alertBadgeTooltip, type ShipmentAlertConfig, type AlertLevel } from "@cdc-sccr/shared"
import { ClientAvatar } from "@/components/client-avatar"
import { formatRoutePath } from "./ShipmentDetailUtils"

// ─── Types ────────────────────────────────────────────────────────────────────

export type ShipmentStatus =
  | "en_planificacion"
  | "planificado"
  | "activo"
  | "en_curso"
  | "finalizado"
  | "cancelado"

type WorkLine = "nacional" | "internacional" | "batea"

export interface ShipmentRow {
  id:                            string
  internalId:                    number
  internationalIds?:             string[]
  status:                        ShipmentStatus
  workLine:                      WorkLine | null
  externalId:                    string | null
  cargoValue:                    string | null
  notes:                         string | null
  scheduledOriginArrivalAt:      string | null
  scheduledDestinationArrivalAt: string | null
  deliveryNoteNumber:            string | null
  loadingWeightKg:               string | null
  unloadingWeightKg:             string | null
  deliveryNoteStatus:            "normal" | "validado" | "reclamado"
  deletedAt:                     string | null
  createdAt:                     string
  updatedAt:                     string
  clientId:                      string | null
  clientName:                    string | null
  clientAvatarKey:               string | null
  clientAvatarUrl?:              string | null
  cargoTypeId:                   string | null
  cargoTypeName:                 string | null
  shipmentKm:                    number | null
  routeId?:                      string | null
  routeCode?:                    string | null
  routeName?:                    string | null
  routeKm?:                      number | null
  routeOriginCityName?:          string | null
  routeTargetCityId?:            string | null
  routeTargetCityName?:           string | null
  routeTargetStateName?:          string | null
  intermediateStopStateNames?:    string | null
  cargoRouteCode?:                string | null
  cargoRouteName?:               string | null
  firstProductId:                string | null
  productNames:                  string | null
  firstTripId:                   string | null
  firstTripDriverId:             string | null
  firstTripTruckPlate:           string | null
  firstTripTrailerPlate:         string | null
  firstTripHasTravelAdvance:     "viaje" | "gnc" | "viaje_gnc" | null
  firstTripMobileId:             string | null
  firstTripMobileNumber:         number | null
  tripCount:                     number
  operatorNames:                 string | null
  driverNames:                   string | null
  firstDriverPhone:              string | null
  senderId:                      string | null
  senderName:                    string | null
  senderCityName:                string | null
  senderCountryName:             string | null
  recipientId:                   string | null
  recipientName:                 string | null
  recipientCityName:             string | null
  recipientCountryName:           string | null
  plannedAt:                     string | null
  originArrivalAt:               string | null
  originLoadingAt:               string | null
  destinationArrivalAt:          string | null
  destinationUnloadingAt:        string | null
  finalizedAt:                   string | null
  // Batea-specific
  loadingWindow:                 string | null
  finalDestinationId:            string | null
  cancelReason:                  string | null
  cancelDescription:             string | null
  billingStatus:                 "controlado" | "liquidado" | "facturado" | "cerrado" | null
  isRouted:                      boolean
  billingTags:                   string | null
}

// ─── Config ───────────────────────────────────────────────────────────────────

// Only active statuses — "finalizado" is excluded from the kanban board
export const KANBAN_STATUSES: ShipmentStatus[] = [
  "en_planificacion",
  "planificado",
  "activo",
  "en_curso",
]

const STATUS_LABELS: Record<ShipmentStatus, string> = {
  en_planificacion: "En planificación",
  planificado:      "Planificado",
  activo:           "Activo",
  en_curso:         "En curso",
  finalizado:       "Finalizado",
  cancelado:        "Cancelado",
}

const COLUMN_ACCENT: Record<ShipmentStatus, string> = {
  en_planificacion: "border-t-slate-400",
  planificado:      "border-t-blue-500",
  activo:           "border-t-emerald-500",
  en_curso:         "border-t-orange-500",
  finalizado:       "border-t-slate-300",
  cancelado:        "border-t-red-400",
}

const COLUMN_COUNT_COLORS: Record<ShipmentStatus, string> = {
  en_planificacion: "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300",
  planificado:      "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  activo:           "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
  en_curso:         "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400",
  finalizado:       "bg-slate-100 text-slate-500",
  cancelado:        "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
}

const WORK_LINE_LABELS: Record<WorkLine, string> = {
  nacional:      "Nacional",
  internacional: "Internacional",
  batea:         "Batea",
}

const WORK_LINE_COLORS: Record<WorkLine, string> = {
  nacional:      "bg-blue-100 text-blue-700",
  internacional: "bg-purple-100 text-purple-700",
  batea:         "bg-amber-100 text-amber-700",
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatCountdown(iso: string): { label: string; urgent: boolean } {
  const diff = new Date(iso).getTime() - Date.now()
  const abs  = Math.abs(diff)
  const past = diff < 0
  const mins  = Math.floor(abs / 60_000)
  const hours = Math.floor(mins / 60)
  const days  = Math.floor(hours / 24)
  let label: string
  if (days > 0)       label = `${past ? "hace" : "en"} ${days}d ${hours % 24}h`
  else if (hours > 0) label = `${past ? "hace" : "en"} ${hours}h ${mins % 60}m`
  else if (mins > 0)  label = `${past ? "hace" : "en"} ${mins}m`
  else                label = past ? "ahora" : "< 1m"
  return { label, urgent: !past && diff < 2 * 3_600_000 }
}

/** Formats elapsed milliseconds as a compact human-readable string (e.g. "3h 20m"). */
function fmtElapsed(ms: number): string {
  const mins  = Math.floor(ms / 60_000)
  const hours = Math.floor(mins / 60)
  const days  = Math.floor(hours / 24)
  if (days > 0)       return `${days}d ${hours % 24}h`
  if (hours > 0)      return `${hours}h ${mins % 60}m`
  if (mins > 0)       return `${mins}m`
  return "< 1m"
}

/** Formats remaining milliseconds as a compact human-readable string (e.g. "1h 30m"). */
function fmtRemaining(ms: number): string {
  return fmtElapsed(Math.abs(ms))
}

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return "—"
  const d   = new Date(iso)
  const pad = (n: number) => String(n).padStart(2, "0")
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${String(d.getFullYear()).slice(-2)} ${pad(d.getHours())}:${pad(d.getMinutes())}`
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function AvatarStack({ names, label }: { names: string; label: string }) {
  const list = names.split("||").filter(Boolean)
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger render={<span className="inline-flex items-center gap-0.5 cursor-default" />}>
          <span className="text-[10px] text-muted-foreground mr-0.5">{label}</span>
          {list.slice(0, 2).map(name => (
            <Avatar key={name} size="sm">
              <AvatarFallback style={{ backgroundColor: avatarColor(name), color: "white" }}>
                {avatarInitials(name)}
              </AvatarFallback>
            </Avatar>
          ))}
          {list.length > 2 && (
            <span className="text-[10px] text-muted-foreground ml-0.5">+{list.length - 2}</span>
          )}
        </TooltipTrigger>
        <TooltipContent>{list.join(", ")}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

function CardCountdown({ iso }: { iso: string }) {
  const [tick, setTick] = useState(0)
  const timerRef        = useRef<ReturnType<typeof setInterval> | null>(null)
  useEffect(() => {
    timerRef.current = setInterval(() => setTick(t => t + 1), 60_000)
    return () => { if (timerRef.current) clearInterval(timerRef.current) }
  }, [])
  void tick
  const { label, urgent } = formatCountdown(iso)
  return (
    <span className={cn(
      "text-[10px] font-medium tabular-nums",
      urgent ? "text-amber-600 dark:text-amber-400" : "text-muted-foreground/60",
    )}>
      {label}
    </span>
  )
}

// ─── AlertBadge ───────────────────────────────────────────────────────────────

/**
 * Derives a human-readable alert description for a shipment card, calculated
 * entirely in the browser. Re-renders every minute via a tick counter.
 *
 * Returns null when no alert is active.
 */
function useAlertBadgeText(
  row: Pick<ShipmentRow, "status" | "plannedAt" | "scheduledOriginArrivalAt" | "originArrivalAt" | "originLoadingAt" | "scheduledDestinationArrivalAt" | "destinationArrivalAt" | "destinationUnloadingAt" | "finalizedAt">,
  alertConfig: ShipmentAlertConfig | undefined,
  now: number,
): { text: string; level: AlertLevel } | null {
  const yellowMs = (alertConfig?.originStaleYellowHours ?? 2) * 3_600_000
  const redMs    = (alertConfig?.originStaleRedHours    ?? 5) * 3_600_000

  if (row.status === "planificado") {
    if (!row.plannedAt) return null
    const elapsed = now - new Date(row.plannedAt).getTime()
    if (elapsed <= 15 * 60_000) return null
    const level = elapsed > 60 * 60_000 ? "critical" : elapsed > 30 * 60_000 ? "warning" : "alert"
    return { text: `${fmtElapsed(elapsed)} sin confirmar`, level }
  }

  if (row.status === "activo") {
    // Rule B — origin stale: most recent between originArrivalAt and originLoadingAt
    const arrMs = row.originArrivalAt  ? new Date(row.originArrivalAt).getTime()  : null
    const lodMs = row.originLoadingAt  ? new Date(row.originLoadingAt).getTime()  : null
    let lastEventMs: number | null = null
    if (arrMs !== null && lodMs !== null) lastEventMs = Math.max(arrMs, lodMs)
    else lastEventMs = arrMs ?? lodMs

    if (lastEventMs !== null) {
      const elapsed = now - lastEventMs
      if (elapsed >= redMs)    return { text: `${fmtElapsed(elapsed)} parado en origen`, level: "critical" }
      if (elapsed >= yellowMs) return { text: `${fmtElapsed(elapsed)} esperando en origen`, level: "alert" }
    }

    // Rule A — scheduled arrival window
    if (!row.scheduledOriginArrivalAt) return null
    if (row.originArrivalAt) return null   // already confirmed — no alert
    const scheduled = new Date(row.scheduledOriginArrivalAt).getTime()
    const diff      = scheduled - now      // positive = future, negative = past
    const window2h  = 2 * 3_600_000
    if (diff > window2h)  return null
    if (diff > 0)         return { text: `Llega en ${fmtRemaining(diff)}`, level: "alert" }
    if (diff > -window2h) return { text: `Demorado ${fmtElapsed(-diff)}`, level: "warning" }
    return { text: `${fmtElapsed(-diff)} sin llegar al origen`, level: "critical" }
  }

  if (row.status === "en_curso") {
    const destArrMs    = row.destinationArrivalAt   ? new Date(row.destinationArrivalAt).getTime()   : null
    const unloadingMs  = row.destinationUnloadingAt ? new Date(row.destinationUnloadingAt).getTime() : null
    const finalizedMs  = row.finalizedAt            ? new Date(row.finalizedAt).getTime()            : null

    // Rule B — destination stale: arrived but not finalized.
    // Reference point: destinationUnloadingAt if set, otherwise destinationArrivalAt.
    if (destArrMs !== null && !finalizedMs) {
      const destYellowMs = (alertConfig?.destinationStaleYellowHours ?? 2) * 3_600_000
      const destRedMs    = (alertConfig?.destinationStaleRedHours    ?? 5) * 3_600_000
      const lastEventMs  = unloadingMs !== null ? Math.max(destArrMs, unloadingMs) : destArrMs
      const elapsed      = now - lastEventMs
      if (elapsed >= destRedMs)    return { text: `${fmtElapsed(elapsed)} en destino sin finalizar`, level: "critical" }
      if (elapsed >= destYellowMs) return { text: `${fmtElapsed(elapsed)} en destino sin finalizar`, level: "alert" }
    }

    // Rule A — scheduled destination arrival window (fires when not yet arrived)
    if (!row.scheduledDestinationArrivalAt) return null
    if (destArrMs !== null) return null   // already arrived — rule A does not apply
    const scheduled = new Date(row.scheduledDestinationArrivalAt).getTime()
    const diff      = scheduled - now
    const window2h  = 2 * 3_600_000
    if (diff > window2h)  return null
    if (diff > 0)         return { text: `Llega en ${fmtRemaining(diff)}`, level: "alert" }
    if (diff > -window2h) return { text: `Demorado ${fmtElapsed(-diff)}`, level: "warning" }
    return { text: `${fmtElapsed(-diff)} sin llegar al destino`, level: "critical" }
  }

  return null
}

const ALERT_BADGE_CLASSES: Record<AlertLevel, string> = {
  alert:    "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300",
  warning:  "bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300",
  critical: "bg-red-100    text-red-800    dark:bg-red-900/40    dark:text-red-300",
}

function AlertBadge({
  row,
  alertConfig,
}: {
  row: Pick<ShipmentRow, "status" | "plannedAt" | "scheduledOriginArrivalAt" | "originArrivalAt" | "originLoadingAt" | "scheduledDestinationArrivalAt" | "destinationArrivalAt" | "destinationUnloadingAt" | "finalizedAt">
  alertConfig: ShipmentAlertConfig | undefined
}) {
  const [tick, setTick] = useState(0)
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  useEffect(() => {
    timerRef.current = setInterval(() => setTick(t => t + 1), 60_000)
    return () => { if (timerRef.current) clearInterval(timerRef.current) }
  }, [])
  void tick

  const badge = useAlertBadgeText(row, alertConfig, Date.now())
  if (!badge) return null

  return (
    <div className="px-2.5 pb-1.5">
      <span className={cn(
        "inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[10px] font-medium",
        ALERT_BADGE_CLASSES[badge.level],
      )}>
        <AlertTriangleIcon className="size-2.5 shrink-0" />
        {badge.text}
      </span>
    </div>
  )
}

// ─── ShipmentCard ─────────────────────────────────────────────────────────────

interface CardProps {
  row:                ShipmentRow
  isAdmin:            boolean
  changingStatusId:   string | null
  dragHandle?:        React.ReactNode
  alertConfig?:       ShipmentAlertConfig | undefined
  onEdit:             (row: ShipmentRow) => void
  onDelete:           (row: ShipmentRow) => void
  onStatusChange:     (id: string, newStatus: ShipmentStatus) => void
  onView:             (id: string) => void
  onToggleRouted:     (id: string, isRouted: boolean) => void
}

export function ShipmentCard({
  row,
  isAdmin,
  changingStatusId,
  dragHandle,
  alertConfig,
  onEdit,
  onDelete,
  onStatusChange,
  onView,
  onToggleRouted,
}: CardProps) {
  const senderLabel    = row.senderName    ?? row.senderCityName    ?? null
  const recipientLabel = row.recipientName ?? row.recipientCityName ?? null
  const firstDriverName = row.driverNames ? row.driverNames.split("||")[0] ?? null : null


  function handleWhatsApp() {
    const formatWindow = (slug: string) => {
      const p = slug.split("-")
      return p.length === 4 ? `${p[0]}:${p[1]} - ${p[2]}:${p[3]}` : slug
    }
    const lines = [
      `*Confirmación de viaje #${row.internalId}*`,
      ``,
      `*Carga:* ${row.productNames ?? "—"}`,
      `*Origen:* ${row.senderCityName ?? "—"}`,
      `*Destino:* ${row.recipientCityName ?? "—"}`,
      `*Cliente:* ${row.clientName ?? "—"}`,
    ]
    if (row.scheduledOriginArrivalAt) {
      const d = new Date(row.scheduledOriginArrivalAt)
      const fechaLarga = d.toLocaleDateString("es-AR", { weekday: "long", day: "numeric", month: "long" })
      lines.push(`*Fecha:* ${fechaLarga.charAt(0).toUpperCase() + fechaLarga.slice(1)}`)
    }
    if (row.loadingWindow) lines.push(`*Ventana de carga:* ${formatWindow(row.loadingWindow)}`)
    if (row.workLine === "batea") lines.push(`*Tipo:* Batea`)
    lines.push(``, `Por favor confirmá tu disponibilidad para este viaje.`)
    const phone = row.firstDriverPhone!.replace(/\D/g, "")
    window.open(`https://wa.me/54${phone}?text=${encodeURIComponent(lines.join("\n"))}`, "_blank")
  }

  const alertLevel = shipmentAlertLevel(row, Date.now(), alertConfig)

  return (
    <div className={cn(
      "group border rounded-xl shadow-sm hover:shadow-md transition-shadow duration-150 overflow-hidden",
      alertLevel ? ALERT_LEVEL_CARD_CLASSES[alertLevel] : "bg-card",
    )}>

      {/* ── Row 1: grip · logo · #id · producto · workLine ── */}
      <div className="flex items-center gap-1.5 px-2.5 pt-2.5 pb-1.5">
        {dragHandle}

        <ClientAvatar clientId={row.clientId} clientName={row.clientName} className="size-6 border border-border/40" />

        <Link
          to={`/shipments/${row.id}`}
          className="font-mono text-[11px] font-bold text-foreground hover:text-primary hover:underline shrink-0"
        >
          #{row.internalId}
        </Link>

        {row.productNames && (
          <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground truncate max-w-20">
            {row.productNames}
          </span>
        )}

        {row.workLine && (
          <span className={cn(
            "ml-auto shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-medium",
            WORK_LINE_COLORS[row.workLine],
          )}>
            {WORK_LINE_LABELS[row.workLine]}
          </span>
        )}
      </div>

      {/* ── Row 2: móvil + chofer ── */}
      <div className="flex items-center gap-1.5 px-2.5 pb-1 min-w-0">
        <TruckIcon className="size-3.5 text-muted-foreground shrink-0" />
        <span className="text-sm font-bold tabular-nums text-foreground leading-none shrink-0">
          {row.firstTripMobileNumber != null ? `#${row.firstTripMobileNumber}` : "—"}
        </span>
        <UserIcon className="size-3 text-muted-foreground shrink-0" />
        <span className="text-xs font-medium truncate text-foreground/75">
          {firstDriverName ?? <span className="text-muted-foreground/40 font-normal italic">Sin chofer</span>}
        </span>
      </div>

      {/* ── Row 3: origen → destino ── */}
      <div className="flex flex-col gap-0 px-2.5 pb-1">
        <div className="flex items-center gap-1 min-w-0">
          <span className="size-1.5 rounded-full bg-muted-foreground/40 shrink-0" />
          {senderLabel
            ? <span className="text-xs font-bold truncate">{senderLabel}</span>
            : <span className="text-xs text-muted-foreground/40 italic">Sin origen</span>
          }
        </div>
        <div className="flex items-center gap-1 min-w-0">
          <ArrowRightIcon className="size-3 text-primary/50 shrink-0" />
          {recipientLabel
            ? <span className="text-xs font-bold truncate">{recipientLabel}</span>
            : <span className="text-xs text-muted-foreground/40 italic">Sin destino</span>
          }
        </div>
      </div>

      {row.workLine === "nacional" && (row.routeId || row.senderCityName || row.recipientCityName) && (
        <div className="mx-2.5 mb-1 grid gap-0.5 rounded-md border border-blue-200 bg-blue-50/60 px-1.5 py-1 text-[10px] dark:border-blue-900/50 dark:bg-blue-950/20">
          <div className="flex min-w-0 items-center gap-1 text-muted-foreground">
            <PackageIcon className="size-3 shrink-0" />
            <span className="truncate">{formatRoutePath(row.senderCityName, null, row.recipientCityName, row.intermediateStopStateNames) || "—"}</span>
          </div>
          <div className="flex min-w-0 items-center gap-1 text-blue-800 dark:text-blue-300">
            <TruckIcon className="size-3 shrink-0" />
            <span className="truncate">{formatRoutePath(row.routeOriginCityName || row.senderCityName, row.routeName, row.routeTargetStateName || row.routeTargetCityName || row.recipientCityName, row.intermediateStopStateNames) || "—"}</span>
          </div>
        </div>
      )}

      {/* ── Alert badge ── */}
      <AlertBadge row={row} alertConfig={alertConfig} />

      {/* ── Row 4: fecha + km ── */}
      <div className="flex items-center justify-between gap-2 px-2.5 pb-2.5">
        <div className="flex items-center gap-1">
          <CalendarIcon className="size-3 text-muted-foreground shrink-0" />
          {row.scheduledOriginArrivalAt ? (
            <>
              <span className="text-[11px] tabular-nums text-muted-foreground">
                {fmtDate(row.scheduledOriginArrivalAt)}
              </span>
              <CardCountdown iso={row.scheduledOriginArrivalAt} />
            </>
          ) : (
            <span className="text-[10px] text-muted-foreground/40">—</span>
          )}
        </div>
        {row.shipmentKm != null && (
          <span className="text-[10px] tabular-nums text-muted-foreground shrink-0">
            {row.shipmentKm.toLocaleString()} km
          </span>
        )}
      </div>

      {/* ── Footer: todo en hover ── */}
      <div className="overflow-hidden transition-all duration-150 ease-out max-h-0 group-hover:max-h-12">
        <div className="flex items-center gap-1 border-t border-border/50 px-2 py-1">

          {/* Primary status action */}
          {row.status === "en_planificacion" && (
            <Button variant="ghost" size="sm"
              className="h-6 px-2 text-xs gap-1 text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 dark:hover:bg-indigo-950/40"
              disabled={changingStatusId === row.id}
              onClick={() => onStatusChange(row.id, "planificado")}>
              <CalendarCheckIcon className="size-3" /> Asignar
            </Button>
          )}
          {row.status === "planificado" && (
            <Button variant="ghost" size="sm"
              className="h-6 px-2 text-xs gap-1 text-violet-600 hover:text-violet-700 hover:bg-violet-50 dark:hover:bg-violet-950/40"
              disabled={changingStatusId === row.id}
              onClick={() => onStatusChange(row.id, "activo")}>
              <CircleCheckIcon className="size-3" /> Confirmar
            </Button>
          )}
          {row.status === "activo" && (
            <Button variant="ghost" size="sm"
              className="h-6 px-2 text-xs gap-1 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 dark:hover:bg-emerald-950/40"
              disabled={changingStatusId === row.id}
              onClick={() => onStatusChange(row.id, "en_curso")}>
              <PlayIcon className="size-3" /> Comenzar
            </Button>
          )}
          {row.status === "en_curso" && (
            <Button variant="ghost" size="sm"
              className="h-6 px-2 text-xs gap-1 text-blue-600 hover:text-blue-700 hover:bg-blue-50 dark:hover:bg-blue-950/40"
              disabled={changingStatusId === row.id}
              onClick={() => onStatusChange(row.id, "finalizado")}>
              <CheckCheckIcon className="size-3" /> Finalizar
            </Button>
          )}

          {/* Ruteado toggle — only for planning/planned columns */}
          {(row.status === "en_planificacion" || row.status === "planificado") && isAdmin && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger
                  type="button"
                  onClick={() => onToggleRouted(row.id, !row.isRouted)}
                  className={cn(
                    "inline-flex items-center justify-center size-6 rounded transition-colors",
                    row.isRouted
                      ? "text-emerald-500 hover:text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-950/40"
                      : "text-muted-foreground/30 hover:text-muted-foreground/60 hover:bg-muted",
                  )}
                >
                  <RouteIcon className="size-3.5" />
                </TooltipTrigger>
                <TooltipContent side="top">
                  {row.isRouted ? "Ruteado — click para quitar" : "No ruteado — click para confirmar"}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}

          {/* WhatsApp + view/edit/delete */}
          <div className="flex items-center gap-0.5 ml-auto">
            {(row.status === "planificado" || row.status === "en_planificacion") && row.firstDriverPhone && (
              <Button variant="ghost" size="icon-sm"
                className="text-green-600 hover:text-green-700 hover:bg-green-50 dark:hover:bg-green-950/40"
                title="Notificar al chofer por WhatsApp"
                onClick={handleWhatsApp}>
                <MessageCircleIcon className="size-3.5" />
              </Button>
            )}
            <Button variant="ghost" size="icon-sm" onClick={() => onView(row.id)} title="Ver detalle">
              <EyeIcon className="size-3.5" />
            </Button>
            {isAdmin && (
              <>
                <Button variant="ghost" size="icon-sm" onClick={() => onEdit(row)} title="Editar">
                  <PencilIcon className="size-3.5" />
                </Button>
                <Button variant="ghost" size="icon-sm"
                  onClick={() => onDelete(row)}
                  className="text-destructive hover:text-destructive hover:bg-destructive/10"
                  title="Eliminar">
                  <Trash2Icon className="size-3.5" />
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

// ─── DraggableCard ────────────────────────────────────────────────────────────

type SharedCardHandlers = {
  isAdmin:          boolean
  alertConfig?:     ShipmentAlertConfig | undefined
  changingStatusId: string | null
  onEdit:           (row: ShipmentRow) => void
  onDelete:         (row: ShipmentRow) => void
  onStatusChange:   (id: string, status: ShipmentStatus) => void
  onView:           (id: string) => void
  onToggleRouted:   (id: string, isRouted: boolean) => void
}

function DraggableCard({ row, changingStatusId, alertConfig, ...handlers }: { row: ShipmentRow } & SharedCardHandlers) {
  // changingStatusId is forwarded explicitly so ShipmentCard can disable its buttons
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id:   row.id,
    data: { row },
  })

  const style = transform
    ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)` }
    : undefined

  const dragHandle = (
    <button
      className="cursor-grab active:cursor-grabbing text-muted-foreground/30 hover:text-muted-foreground/70 transition-colors touch-none shrink-0 -ml-0.5"
      aria-label="Arrastrar"
      {...attributes}
      {...listeners}
    >
      <GripVerticalIcon className="size-3.5" />
    </button>
  )

  return (
    <div ref={setNodeRef} style={style} className={isDragging ? "opacity-30" : undefined}>
      <ShipmentCard
        row={row}
        dragHandle={dragHandle}
        changingStatusId={changingStatusId}
        alertConfig={alertConfig}
        {...handlers}
      />
    </div>
  )
}

// ─── DroppableColumn ─────────────────────────────────────────────────────────

function DroppableColumn({
  status,
  label,
  rows,
  ...handlers
}: { status: ShipmentStatus; label: string; rows: ShipmentRow[] } & SharedCardHandlers) {
  const { setNodeRef, isOver } = useDroppable({ id: status })

  // Count cards with any alert level (pass alertConfig for stale-origin rule)
  const staleCount = rows.filter(r => shipmentAlertLevel(r, Date.now(), handlers.alertConfig) !== null).length

  return (
    <div className={cn(
      "flex flex-col rounded-lg border bg-muted/30 border-t-2",
      COLUMN_ACCENT[status],
    )}>
      {/* Column header */}
      <div className="flex items-center justify-between px-3 py-2.5 border-b border-border/50">
        <span className="text-sm font-semibold">{label}</span>
        <div className="flex items-center gap-1.5">
          {staleCount > 0 && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger render={<span />}>
                  <span className={cn("inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[10px] font-bold tabular-nums", ALERT_LEVEL_BADGE_CLASSES.warning)}>
                    {alertBadgeLabel(staleCount, status)}
                  </span>
                </TooltipTrigger>
                <TooltipContent>{alertBadgeTooltip(status)}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          <span className={cn(
            "inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold tabular-nums",
            COLUMN_COUNT_COLORS[status],
          )}>
            {rows.length}
          </span>
        </div>
      </div>

      {/* Drop zone */}
      <div
        ref={setNodeRef}
        className={cn(
          "flex flex-col gap-2 p-2 overflow-y-auto max-h-[calc(100vh-220px)] min-h-[80px] rounded-b-lg transition-colors duration-150",
          isOver && "bg-primary/5",
        )}
      >
        {rows.length === 0 && !isOver ? (
          <p className="text-[11px] text-muted-foreground/50 text-center py-6">Sin cargas</p>
        ) : (
          rows.map(row => (
            <DraggableCard key={row.id} row={row} {...handlers} />
          ))
        )}
      </div>
    </div>
  )
}

// ─── ShipmentsKanban ──────────────────────────────────────────────────────────

interface KanbanProps extends SharedCardHandlers {
  items:        ShipmentRow[]
  alertConfig?: ShipmentAlertConfig | undefined
}

export default function ShipmentsKanban({ items, ...handlers }: KanbanProps) {
  const [activeRow, setActiveRow] = useState<ShipmentRow | null>(null)

  const sensors = useSensors(
    useSensor(PointerSensor, {
      // Require 8px movement before drag starts — prevents conflict with buttons/selects
      activationConstraint: { distance: 8 },
    }),
  )

  function handleDragStart(event: DragStartEvent) {
    const row = event.active.data.current?.row as ShipmentRow | undefined
    setActiveRow(row ?? null)
  }

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event
    setActiveRow(null)
    if (!over) return
    const newStatus = over.id as ShipmentStatus
    const row       = active.data.current?.row as ShipmentRow | undefined
    if (row && row.status !== newStatus) {
      handlers.onStatusChange(row.id, newStatus)
    }
  }

  const columns = KANBAN_STATUSES.map(status => ({
    status,
    label: STATUS_LABELS[status],
    rows:  items.filter(r => r.status === status),
  }))

  return (
    <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3 items-start">
        {columns.map(({ status, label, rows }) => (
          <DroppableColumn
            key={status}
            status={status}
            label={label}
            rows={rows}
            {...handlers}
          />
        ))}
      </div>

      {/* Floating card preview while dragging */}
      <DragOverlay dropAnimation={{ duration: 180, easing: "ease" }}>
        {activeRow ? (
          <div className="rotate-1 shadow-2xl opacity-95">
            <ShipmentCard
              row={activeRow}
              isAdmin={handlers.isAdmin}
              changingStatusId={handlers.changingStatusId}
              alertConfig={handlers.alertConfig}
              onEdit={handlers.onEdit}
              onDelete={handlers.onDelete}
              onStatusChange={handlers.onStatusChange}
              onView={handlers.onView}
              onToggleRouted={handlers.onToggleRouted}
            />
          </div>
        ) : null}
      </DragOverlay>

    </DndContext>
  )
}
